/**
 * F-015 — Planet queue.
 *
 * Holds a fixed-length lookahead window of upcoming planet tiers. The
 * generator is injected (defaults to a weighted RNG over tiers 0..3) so
 * F-029 (daily challenge) can swap in a seeded version without touching
 * queue mechanics. peek/advance keep the window full.
 */

const DEFAULT_LOOKAHEAD = 3;

export type TierGenerator = () => number;

export interface PlanetQueue {
  size: number;
  /** Snapshot of upcoming tiers (head = next to launch). */
  peek(): ReadonlyArray<number>;
  /** Pop the head and refill the tail. Returns the popped tier. */
  advance(): number;
  /** Replace the generator + refill (e.g., switching modes). */
  reseed(generator: TierGenerator): void;
}

export interface PlanetQueueOptions {
  lookahead?: number;
  generator?: TierGenerator;
}

// Suika-style — only the smallest meteors come from the queue; larger
// tiers are reached through merging.
const DEFAULT_TIER_WEIGHTS: ReadonlyArray<number> = [55, 30, 12, 3];

export function defaultTierGenerator(): TierGenerator {
  const total = DEFAULT_TIER_WEIGHTS.reduce((s, n) => s + n, 0);
  return () => {
    let pick = Math.random() * total;
    for (let i = 0; i < DEFAULT_TIER_WEIGHTS.length; i += 1) {
      pick -= DEFAULT_TIER_WEIGHTS[i]!;
      if (pick <= 0) return i;
    }
    return DEFAULT_TIER_WEIGHTS.length - 1;
  };
}

export function createPlanetQueue(
  options: PlanetQueueOptions = {},
): PlanetQueue {
  const lookahead = options.lookahead ?? DEFAULT_LOOKAHEAD;
  if (lookahead <= 0) {
    throw new Error("planet-queue: lookahead must be positive");
  }
  let generator = options.generator ?? defaultTierGenerator();
  const window: number[] = [];

  const fill = (): void => {
    while (window.length < lookahead) {
      window.push(generator());
    }
  };

  const peek = (): ReadonlyArray<number> => {
    fill();
    return [...window];
  };

  const advance = (): number => {
    fill();
    const head = window.shift()!;
    fill();
    return head;
  };

  const reseed = (next: TierGenerator): void => {
    generator = next;
    window.length = 0;
    fill();
  };

  fill();

  return { size: lookahead, peek, advance, reseed };
}

export const __testing = { DEFAULT_LOOKAHEAD, DEFAULT_TIER_WEIGHTS };
